import getConfig from "../../../config";
import { DEV_URL } from "../../../utils/requests";
/** Helpers */
const mapCommodity = (c) => (c === "1" ? "Electricity" : "Gas");

/** Build a lookup: `${state}|${utility}` -> ofN  */
export const buildOfNLookup = (markets) =>
  new Map(markets.map((m) => [`${m.utility}`, m.ofN]));

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Leaderboard: fetch + parse to markets[] (state, utility, commodity, ofN, suppliers[]) */
export async function fetchLeaderboardParsed(accessToken, clientId) {
  const config = getConfig();
  const urlBase = config?.PricingMatrix?.SocketUrl;

  const headers = {
    "Content-Type": "application/json",
    client_id: config?.PricingMatrix?.SocketClientId,
    Authorization: `Bearer ${accessToken}`,
  };

  const body = JSON.stringify({
    EnerexClientIdentifier: clientId,
    EffectiveFrom:'',
});

  const res = await fetch(`${urlBase}/common/api/leaderboard`, {
    method: "POST",
    headers,
    body,
  });
  if (!res.ok) return null;

  const data = await res.json();
  const rankings = data[0]?.Rankings || [];

  const markets = rankings.map((r) => {
    const commodity = mapCommodity(r.Commodity);
    const sorted = (r.SupplierRank || []).slice().sort((a, b) => a.Rank - b.Rank);
    const ofN = sorted.reduce((max, s) => Math.max(max, s.Rank || 1), 1);

    const suppliers = sorted.map((s) => ({
      rank: s.Rank,
      name: s.SupplierName,
      price: s.Price,
      supplierId: s.SupplierEnerexIdentifier,
      // `clientId` is the "my supplier" in context
      isEngie:
        (s.SupplierEnerexIdentifier || "").toLowerCase() ===
        (clientId || "").toLowerCase(),
    }));

    return {
      
      commodity,
      utility: r.Utility,
      ofN,
      suppliers,
    };
  });

  return markets;
}

/** Best markets: fetch + parse to [{ state, utility, commodity, rank, name, value, supplierId, ofN? }] */
export async function fetchBestMarketsParsed(accessToken, clientId, ofNLookup) {
  const config = getConfig();
  const urlBase = config?.PricingMatrix?.SocketUrl;

  const headers = {
    "Content-Type": "application/json",
    client_id: config?.PricingMatrix?.SocketClientId,
    Authorization: `Bearer ${accessToken}`,
  };

  const body = JSON.stringify({
    EnerexClientIdentifier: clientId,
    EffectiveFrom: '', 
  });

  const res = await fetch(`${urlBase}/common/pricing/top5/best`, {
    method: "POST",
    headers,
    body,
  });
  if (!res.ok) return null;

  const data = await res.json();

  const items = (data || []).flatMap((block) => {
    const commodity = mapCommodity(block?.Commodity);
    return (block?.Rankings || []).flatMap((r) =>
      (r?.SupplierRankings || [])
        .filter((s) => s?.Rank != null)
        .map((s) => ({
          
          utility: r.Utility?.trim?.() ?? r.Utility,
          commodity,
          rank: s.Rank,
          name: s.SupplierName,
          value: s.BestDelta ?? s.bestdelta ?? null,
          supplierId: s.SupplierEnerexIdentifier,
          ofN: ofNLookup?.get?.(`${r.Utility}`),
          BestDelta: s.BestDelta,
          NextBetterPrice: s.NextBetterPrice,
          NextWorsePrice: s.NextWorsePrice,
          WorstDelta: s.WorstDelta,
          CurrPrice: s.CurrPrice,

          }))
    );
  });

  return items;
}

/** Worst markets: fetch + parse (same shape as best) */
export async function fetchWorstMarketsParsed(accessToken, clientId, ofNLookup) {
  const config = getConfig();
  const urlBase = config?.PricingMatrix?.SocketUrl;

  const headers = {
    "Content-Type": "application/json",
    client_id: config?.PricingMatrix?.SocketClientId,
    Authorization: `Bearer ${accessToken}`,
  };

  const body = JSON.stringify({
    EnerexClientIdentifier: clientId,
    EffectiveFrom: '', 
  });

  const res = await fetch(`${urlBase}/common/pricing/top5/worst`, {
    method: "POST",
    headers,
    body,
  });
  if (!res.ok) return null;

  const data = await res.json();

  const items = (data || []).flatMap((block) => {
    const commodity = mapCommodity(block?.Commodity);
    return (block?.Rankings || []).flatMap((r) =>
      (r?.SupplierRankings || [])
        .filter((s) => s?.Rank != null)
        .map((s) => ({
         
          utility: r.Utility?.trim?.() ?? r.Utility,
          commodity,
          rank: s.Rank,
          name: s.SupplierName,
          value: s.BestDelta ?? s.bestdelta ?? null,
          supplierId: s.SupplierEnerexIdentifier,
          ofN: ofNLookup?.get?.(`${r.Utility}`),
           BestDelta: s.BestDelta,
          NextBetterPrice: s.NextBetterPrice,
          NextWorsePrice: s.NextWorsePrice,
          WorstDelta: s.WorstDelta,
          CurrPrice: s.CurrPrice,
        }))
    );
  });

  return items;
}

/** Licenses: POST to your localhost API; returns array of license items */
export async function fetchLicenses(accessToken, clientId) {
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
  };

  const body = JSON.stringify({
    PageNumber: 1,
    PageSize: 50,
    SearchTerm: "",
    clientId: clientId,
  });

  const res = await fetch(`${DEV_URL}/api/license/getall`, {
    method: "POST",
    headers,
    body,
  });

  if (!res.ok) return null;

  const data = await res.json();
  return data?.license || [];
}


// api/api.js
export const fetchFollowedCompanies = async (accessToken, clientId, secureId, baseUrl) => {
  const res = await fetch(
    `${DEV_URL}/api/client/${clientId}/user/subscribed-companiesDetails?secureId=${encodeURIComponent(secureId)}`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  if (!res.ok) return null;
  return await res.json();
};

/** MyMarkets leaderboard (new v2 endpoint) */
// export async function fetchMyMarkets2(accessToken, clientId, effectiveFrom) {
//   const config = getConfig();
//   const urlBase = config?.PricingMatrix?.SocketUrl;

//   const headers = {
//     "Content-Type": "application/json",
//     client_id: config?.PricingMatrix?.SocketClientId,
//     Authorization: `Bearer ${accessToken}`,
//   };

//   const body = JSON.stringify({
//     EffectiveFrom: "", //effectiveFrom,
//   });

//   const res = await fetch(`${urlBase}/common/api/v2/leaderboard`, {
//     method: "POST",
//     headers,
//     body,
//   });
//   if (!res.ok) throw new Error("Failed to fetch MyMarkets leaderboard");

//   return await res.json();
// }

export async function fetchMyMarkets(effectiveFrom) {
  try {
    const res = await fetch(
      `${DEV_URL}/public/PublicPage/firstToWorst?effectiveFrom=${effectiveFrom || ""}`
    );

    if (!res.ok) {
      console.error("Failed to fetch MyMarkets leaderboard", res.statusText);
      return null; // gracefully return null instead of throwing
    }

    return await res.json();
  } catch (err) {
    console.error("Error fetching MyMarkets leaderboard:", err);
    return null; // or return [] if you want an empty array
  }
}

